import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Stack
} from '@mui/material';
// import AddIcon from '@mui/icons-material/Add';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { getAllReferrals, admitReferral } from '../../services/careCoordinationService';
import { DatePicker } from '@mui/x-date-pickers';



/**
 * ReferralAdmission Component
 * Displays referrals with ACTIVE or PENDING status that are ready for patient admission.
 * 
 * @param {Object} props - Component props
 * @param {string} props.patientId - Optional patient ID to filter referrals
 * @param {Array} props.patients - Array of patients for reference
 * @param {string} props.facilityId - Facility ID to fetch referrals for
 * @returns {JSX.Element} Referral admission interface
 */
export default function ReferralAdmission({ patientId, patients, facilityId }) {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [isAdmissionDialogOpen, setIsAdmissionDialogOpen] = useState(false);
  const [admissionForm, setAdmissionForm] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const loadActivePendingReferrals = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      if (!facilityId) {
        setError('No facility selected. Please ensure you have access to a facility.');
        return;
      }
      
      // Get referrals for the user's default facility and filter for active/pending ones
      const allReferrals = await getAllReferrals(facilityId);
      
      // Ensure allReferrals is an array
      if (!Array.isArray(allReferrals)) {
        console.warn('ReferralAdmission: getAllReferrals returned non-array:', allReferrals);
        setReferrals([]);
        return;
      }
      
      // Debug: Log the structure of first few referrals
      if (allReferrals.length > 0) {
        console.log('ReferralAdmission: Sample referral structure:', allReferrals.slice(0, 2));
        console.log('ReferralAdmission: Status fields found:', allReferrals.map(ref => ({
          id: ref.id,
          status: ref.status,
          patientName: ref.patient?.name || 'Unknown Patient',
          patientId: ref.patient?.id,
          from: ref.from,
          reason: ref.reason,
          priority: ref.priority
        })));
      }
      
      // Count referrals by status for debugging
      const statusCounts = {};
      allReferrals.forEach(ref => {
        const status = ref.status || 'UNKNOWN';
        statusCounts[status] = (statusCounts[status] || 0) + 1;
      });
      console.log('ReferralAdmission: Referral status distribution:', statusCounts);
      
      const activePendingReferrals = allReferrals.filter(ref => {
        const status = ref.status;
        
        // Handle edge cases
        if (!status) {
          console.warn(`Referral ${ref.id}: Missing status field, excluding from admission list`);
          return false;
        }
        
        const isActiveOrPending = status === 'ACTIVE' || status === 'PENDING';
        return isActiveOrPending;
      });
      
      setReferrals(activePendingReferrals);
    } catch (err) {
      console.error('ReferralAdmission: Error loading referrals:', err);
      setError(`Failed to load referrals: ${err.message || err}`);
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  // Debug: Log when component receives facilityId
  useEffect(() => {
    console.log('ReferralAdmission: Received props:', { patientId, facilityId, patientsCount: patients?.length });
  }, [patientId, facilityId, patients]);

  useEffect(() => {
    if (facilityId) {
      loadActivePendingReferrals();
    }
  }, [facilityId, loadActivePendingReferrals]);

  const handleAdmitReferral = (referral) => {
    setSelectedReferral(referral);
    
    // Pre-populate form with referral data
    const initialForm = {
      firstName: referral.patient?.firstName || '',
      lastName: referral.patient?.lastName || '',
      dateOfBirth: referral.patient?.dateOfBirth ? 
        new Date(referral.patient.dateOfBirth).toISOString().split('T')[0] : '',
      gender: referral.patient?.gender || '',
      contact: referral.contact || '',
      email: referral.email || '',
      address: referral.address || '',
      bloodType: referral.bloodType || '',
      allergies: referral.allergies || [],
      chronicConditions: referral.chronicConditions || [],
      // Additional fields required for patient creation
      emergencyContact: {
        name: '',
        relationship: '',
        phone: ''
      },
      insurance: {
        provider: '',
        policyNumber: '',
        groupNumber: ''
      },
      primaryLanguage: 'English',
      maritalStatus: '',
      occupation: '',
      // Referral-specific fields
      referralReason: referral.reason || '',
      referralSource: referral.from || '',
      referralNotes: referral.notes || ''
    };
    
    setAdmissionForm(initialForm);
    setIsAdmissionDialogOpen(true);
  };

  const handleFormChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setAdmissionForm(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setAdmissionForm(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSubmitAdmission = async () => {
    try {
      setSubmitting(true);
      setError('');
      
      const result = await admitReferral(selectedReferral.id, admissionForm);
      
      // Close dialog and refresh referrals
      setIsAdmissionDialogOpen(false);
      setSelectedReferral(null);
      setAdmissionForm({});
      
      // Show success message
      alert(`Referral admitted successfully! Patient ID: ${result.patientId}`);
      
      // Refresh the referrals list
      await loadActivePendingReferrals();
      
    } catch (err) {
      setError(err.message);
      console.error('Error admitting referral:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'primary';
      case 'PENDING': return 'warning';
      case 'ADMITTED': return 'success';
      case 'CANCELLED': return 'error';
      case 'COMPLETED': return 'info';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'URGENT': return 'error';
      case 'HIGH': return 'warning';
      case 'ROUTINE': return 'info';
      default: return 'default';
    }
  };

  if (!facilityId) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="warning">
          No facility selected. Please ensure you have access to a facility to view referrals.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading active and pending referrals...</Typography>
      </Box>
    );
  }

  return (
    <Box>


      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Referrals Ready for Admission ({referrals.length})
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Only showing referrals with ACTIVE or PENDING status that are ready for patient admission.
          </Typography>
          

          
          {referrals.length === 0 ? (
            <Typography color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
              No active or pending referrals found. All referrals may have been processed or are in other statuses.
            </Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Patient</TableCell>
                    <TableCell>Referral Source</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Referral Date</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {referrals.map((referral) => (
                    <TableRow key={referral.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {referral.patient?.name || 'Unknown Patient'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {referral.patient?.dateOfBirth ? 
                            new Date(referral.patient.dateOfBirth).toLocaleDateString() : 
                            'DOB not available'
                          }
                        </Typography>
                      </TableCell>
                      <TableCell>{referral.from}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200 }}>
                          {referral.reason}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={referral.priority} 
                          color={getPriorityColor(referral.priority)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={referral.status} 
                          color={getStatusColor(referral.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {referral.referralDate ? 
                          (referral.referralDate._seconds ? 
                            new Date(referral.referralDate._seconds * 1000).toLocaleDateString() :
                            new Date(referral.referralDate).toLocaleDateString()
                          ) : 
                          referral.createdAt ? 
                            (referral.createdAt._seconds ? 
                              new Date(referral.createdAt._seconds * 1000).toLocaleDateString() :
                              new Date(referral.createdAt).toLocaleDateString()
                            ) : 'N/A'
                        }
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Admit Referral">
                          <IconButton 
                            color="primary"
                            onClick={() => handleAdmitReferral(referral)}
                            size="small"
                          >
                            <PersonAddIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Admission Dialog */}
      <Dialog 
        open={isAdmissionDialogOpen} 
        onClose={() => !submitting && setIsAdmissionDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Admit Referral: {selectedReferral?.patient?.name || 'New Patient'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Patient Information
            </Typography>
            
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="First Name"
                value={admissionForm.firstName || ''}
                onChange={(e) => handleFormChange('firstName', e.target.value)}
                required
              />
              <TextField
                fullWidth
                label="Last Name"
                value={admissionForm.lastName || ''}
                onChange={(e) => handleFormChange('lastName', e.target.value)}
                required
              />
            </Stack>
            
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <DatePicker
                label="Date of Birth"
                value={admissionForm.dateOfBirth ? new Date(admissionForm.dateOfBirth) : null}
                onChange={(date) => handleFormChange('dateOfBirth', date ? date.toISOString().split('T')[0] : '')}
                slotProps={{
                  textField: { fullWidth: true, required: true }
                }}
              />
              <FormControl fullWidth>
                <InputLabel>Gender</InputLabel>
                <Select
                  value={admissionForm.gender || ''}
                  onChange={(e) => handleFormChange('gender', e.target.value)}
                  label="Gender"
                >
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                  <MenuItem value="prefer-not-to-say">Prefer not to say</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Contact Phone"
                value={admissionForm.contact || ''}
                onChange={(e) => handleFormChange('contact', e.target.value)}
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={admissionForm.email || ''}
                onChange={(e) => handleFormChange('email', e.target.value)}
              />
            </Stack>
            
            <TextField
              fullWidth
              label="Address"
              multiline
              rows={2}
              value={admissionForm.address || ''}
              onChange={(e) => handleFormChange('address', e.target.value)}
              sx={{ mb: 2 }}
            />
            
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Emergency Contact
            </Typography>
            
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Emergency Contact Name"
                value={admissionForm.emergencyContact?.name || ''}
                onChange={(e) => handleFormChange('emergencyContact.name', e.target.value)}
              />
              <TextField
                fullWidth
                label="Relationship"
                value={admissionForm.emergencyContact?.relationship || ''}
                onChange={(e) => handleFormChange('emergencyContact.relationship', e.target.value)}
              />
              <TextField
                fullWidth
                label="Emergency Contact Phone"
                value={admissionForm.emergencyContact?.phone || ''}
                onChange={(e) => handleFormChange('emergencyContact.phone', e.target.value)}
              />
            </Stack>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Insurance Information
            </Typography>
            
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Insurance Provider"
                value={admissionForm.insurance?.provider || ''}
                onChange={(e) => handleFormChange('insurance.provider', e.target.value)}
              />
              <TextField
                fullWidth
                label="Policy Number"
                value={admissionForm.insurance?.policyNumber || ''}
                onChange={(e) => handleFormChange('insurance.policyNumber', e.target.value)}
              />
              <TextField
                fullWidth
                label="Group Number"
                value={admissionForm.insurance?.groupNumber || ''}
                onChange={(e) => handleFormChange('insurance.groupNumber', e.target.value)}
              />
            </Stack>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Referral Information
            </Typography>
            
            <TextField
              fullWidth
              label="Referral Reason"
              multiline
              rows={2}
              value={admissionForm.referralReason || ''}
              onChange={(e) => handleFormChange('referralReason', e.target.value)}
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              label="Additional Notes"
              multiline
              rows={3}
              value={admissionForm.referralNotes || ''}
              onChange={(e) => handleFormChange('referralNotes', e.target.value)}
              sx={{ mb: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setIsAdmissionDialogOpen(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitAdmission}
            variant="contained"
            disabled={submitting}
            startIcon={<PersonAddIcon />}
          >
            {submitting ? 'Admitting...' : 'Admit Patient'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
